import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { sb } from '../supabase';
import { resolveFactoryEntry, ENTRY } from '../lib/factoryEntry';
import { waLink } from '../lib/maabarContact';
import { CUSTOMIZATION_CHIPS, labelFor } from '../lib/requestFormOptions';
import QuoteFields, { SAMPLE_ASKED, validateQuoteCore } from '../components/quote/QuoteFields';
import DeliveryCapability from '../components/quote/DeliveryCapability';
import { quoteStrings } from '../components/quote/quoteStrings';
import { useAdminCnyRate } from '../lib/useAdminCnyRate';
import {
  loadDeliveryTerms, saveDeliveryTerms, allowedBasesFor, defaultBasisFor,
} from '../lib/supplierDeliveryTerms';
import { displayCategoryForCode } from '../lib/factoryCategories';
import {
  supplierTermRows, prefillPriceBasis, deliveryAskText, deliveryShortfallText,
} from '../lib/supplierRequestTerms';
import { isOfferNoteContactError, offerNoteContactMessage } from '../lib/offerNoteContact';

// Factory-facing request page, opened from the WhatsApp/email link.
//
// THE REQUEST IS PUBLIC AND SHOWN FIRST. Claiming is the price of REPLYING, not
// of reading (Majeed, 2026-09-16, reversing the 2026-09-06 decision that
// 1fcc4a7 implemented). Friction sits at the moment of interest: anyone holding
// the slug reads the brief, and pressing "respond" is what routes an unclaimed
// factory to /claim/<claim_slug>?next=/f/<slug>&respond=1 or a signed-out owner
// to login with the same next.
//
// The resolver is still the SAME one /factory/inbox/:slug uses and still returns
// the same destinations — only the moment it is acted on moved. The one case
// that remains a wall on load is OTHER_ACCOUNT: showing the brief to someone
// signed in as a different supplier would leak another factory's work queue.
//
// What makes this safe to open up is that nothing here carries buyer contact
// details. No column does — audited, 2026-09-16 — and the one free-text field
// that COULD, the AI-written brief, is scrubbed at generation (the maabar-ai
// prompt) and again at read (mask_contact_details, migration 20260916144335).
//
// The inline phone+password signup it used to carry is gone: it was the last
// surface creating accounts with role:'supplier' in options.data, which the claim
// page deliberately stopped doing so an abandoned flow leaves no supplier account.
//
// Factory audience → en/zh only (not the buyer's app language). Placeholder
// styling (combined design pass later).
const C = {
  en: {
    heading: 'Quote request', from: 'via Maabar', details: 'Request details', factory: 'Factory', product: 'Product',
    specs: 'Specifications', customization: 'Customization', attachments: 'Attachments',
    category: 'Category',
    respond: 'Respond to this request', haveAccount: 'Already registered? Sign in', newAccount: 'New factory? Register',
    // Shown UNDER the button when replying will route through claim or sign-in.
    // The request above is already readable; this says what the next tap costs,
    // rather than letting the redirect come as a surprise.
    respondNoteClaim: "You'll claim your factory page first — it takes about a minute, then you come straight back here.",
    respondNoteLogin: "You'll sign in first, then come straight back here.",
    phone: 'Phone (international, e.g. +8613800138000)', password: 'Password', company: 'Factory / company name',
    register: 'Register & continue', signin: 'Sign in & continue', working: 'Please wait…',
    offerTitle: 'Submit your offer', price: 'Unit price *', currency: 'Currency', delivery: 'Delivery (days)',
    moq: 'MOQ (min. order)', note: 'Note to Maabar (optional)', submit: 'Submit offer', submitting: 'Submitting…',
    doneT: 'Offer submitted', doneB: 'Thank you. The Maabar team will review your offer and get back to you.',
    expired: 'This link has expired.', already: 'An offer has already been submitted for this request.',
    // The REQUEST is over — a different fact from the LINK having expired, and
    // the difference matters to the reader: one is about their invitation, the
    // other about the buyer's decision. Nothing they can do fixes either, but
    // being told the right one is the difference between "I missed it" and
    // "it was withdrawn".
    overTitle: 'This request is closed',
    overBody: 'The buyer is no longer collecting quotes on this request, so it can no longer receive offers. Nothing is needed from you.',
    notFound: 'Request not found.', privacy: 'Buyer contact details are shared only after the deal proceeds through Maabar.',
    errPrice: 'Please enter a valid unit price.',
    errPhone: 'Please enter a valid phone number in international format (e.g. +8613800138000).',
    errPassword: 'Please enter a password of at least 6 characters.',
    errExists: 'This phone number is already registered — switch to “Sign in”.',
    errCreds: 'Phone number or password is incorrect.',
    errNeedRegister: 'Please register before submitting an offer.',
    errExpired: 'This link has expired.',
    errClaimed: 'This request was already claimed by another account.',
    errIdentityMismatch: "The phone or email you signed in with does not match the contact we hold for this factory. Sign in with the factory's registered phone or email, or contact us.",
    errNoContactOnFile: 'We hold no phone or email for this factory, so ownership cannot be verified automatically. Please contact us.',
    errNeedClaim: 'Claim your factory page first, then come back to reply to this request.',
    errFactoryOther: 'This factory page belongs to another account.',
    errAlreadyOffer: 'An offer has already been submitted for this request.',
    errAuth: 'Could not sign you in. Please check your details and try again.',
    errLoad: 'Could not load this request. Please try again.',
    errGeneric: 'Something went wrong. Please try again.',
    otherTitle: 'This factory page is registered to another account',
    otherBody: "We can't show any details about that account. If it belongs to your company, sign in with that account — or contact us on WhatsApp and we'll help.",
    otherSignout: 'Sign in with a different account',
    otherSupport: 'Contact support on WhatsApp',
    waText: 'Hello, I need help accessing our factory page on Maabar.',
  },
  zh: {
    heading: '报价请求', from: '来自 Maabar', details: '需求详情', factory: '工厂', product: '产品',
    specs: '规格', customization: '定制', attachments: '附件',
    category: '类别',
    respond: '回复此需求', haveAccount: '已注册？登录', newAccount: '新工厂？注册',
    respondNoteClaim: '您需要先认领工厂主页 — 大约一分钟，之后会直接返回此页面。',
    respondNoteLogin: '您需要先登录，之后会直接返回此页面。',
    phone: '电话（国际格式，如 +8613800138000）', password: '密码', company: '工厂／公司名称',
    register: '注册并继续', signin: '登录并继续', working: '请稍候…',
    offerTitle: '提交您的报价', price: '单价 *', currency: '货币', delivery: '交货（天）',
    moq: '起订量 (MOQ)', note: '给 Maabar 的备注（可选）', submit: '提交报价', submitting: '提交中…',
    doneT: '报价已提交', doneB: '谢谢。Maabar 团队将审核您的报价并与您联系。',
    expired: '此链接已过期。', already: '此需求已提交过报价。',
    overTitle: '此需求已关闭',
    overBody: '买家已停止为此需求收集报价，因此不能再提交报价。您无需再做任何事。',
    notFound: '未找到需求。', privacy: '买家联系方式仅在交易通过 Maabar 推进后共享。',
    errPrice: '请输入有效的单价。',
    errPhone: '请输入有效的电话号码（国际格式，如 +8613800138000）。',
    errPassword: '请输入至少 6 位的密码。',
    errExists: '该电话号码已注册，请切换到“登录”。',
    errCreds: '电话号码或密码不正确。',
    errNeedRegister: '提交报价前请先注册。',
    errExpired: '此链接已过期。',
    errClaimed: '此需求已被其他账户认领。',
    errIdentityMismatch: '您登录所用的手机号或邮箱与我们记录中该工厂的联系方式不一致。请使用工厂登记的手机号或邮箱登录，或联系我们。',
    errNoContactOnFile: '我们的记录中没有该工厂的手机号或邮箱，无法自动核实所有权。请联系我们。',
    errNeedClaim: '请先认领您的工厂主页，然后返回回复此需求。',
    errFactoryOther: '此工厂主页属于其他账户。',
    errAlreadyOffer: '此需求已提交过报价。',
    errAuth: '无法登录，请检查信息后重试。',
    errLoad: '无法加载此需求，请重试。',
    errGeneric: '出错了，请重试。',
    otherTitle: '此工厂主页已由其他账户注册',
    otherBody: '我们无法显示该账户的任何信息。如果这是贵公司的主页，请使用该账户登录；或通过 WhatsApp 联系我们，我们会协助您。',
    otherSignout: '用其他账户登录',
    otherSupport: '通过 WhatsApp 联系客服',
    waText: '您好，我需要帮助访问我们在 Maabar 的工厂主页。',
  },
};

export default function FactoryRequestView() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [lang, setLang] = useState('en');
  const t = C[lang];
  // The SHARED quote-core labels, so this door words the same fields the same
  // way the dashboard and the public RFQ link do. This page's own copy (C)
  // stays for everything else on it.
  const qt = quoteStrings(lang);
  const cnyPerUsd = useAdminCnyRate();

  // ?respond=1 — set by this page on the way out to claim/sign-in, read on the
  // way back. It means "this factory already pressed Respond", nothing more; it
  // grants no access on its own, because the resolver still has to say the
  // visitor may register before anything acts on it.
  const wantsRespond = searchParams.get('respond') === '1';

  /**
   * Add respond=1 to the ?next= INSIDE a claim/login URL.
   *
   * The flag has to travel on the inner path, not the outer one: the claim page
   * navigates to whatever `next` holds, so a flag on the outer URL would be
   * dropped at the redirect. Parsed rather than string-concatenated because
   * `next` is already percent-encoded, and appending "&respond=1" to an encoded
   * value produces a next the claim page cannot decode.
   */
  const withRespondFlag = (to) => {
    try {
      const url = new URL(to, window.location.origin);
      const inner = url.searchParams.get('next');
      if (!inner) return to;
      const innerUrl = new URL(inner, window.location.origin);
      innerUrl.searchParams.set('respond', '1');
      url.searchParams.set('next', `${innerUrl.pathname}${innerUrl.search}`);
      return `${url.pathname}${url.search}`;
    } catch {
      // A destination we cannot parse still has to work — losing the flag costs
      // the factory one extra click, losing the redirect costs them the offer.
      return to;
    }
  };

  // Map raw RPC/exception text to a friendly, localized message (never show raw).
  const mapRpc = (msg) => {
    // The database refuses a note with contact details (SECURITY_BACKLOG #17).
    if (isOfferNoteContactError(msg)) return offerNoteContactMessage(lang);
    const m = String(msg || '').toLowerCase();
    if (m.includes('expired')) return t.errExpired;
    // Both raised by register_factory_invite as of migration 20260917130000,
    // which stopped that function granting factory ownership. Neither SHOULD be
    // reachable from this page — resolveFactoryEntry sends an unclaimed factory
    // to /claim, and doRegister runs only for ENTRY.OWNER — but "should be
    // unreachable" is exactly what was true of the ownership bind that migration
    // removed. Unmapped they render as "Something went wrong", which tells a
    // factory nothing about the one action that would fix it.
    if (m.includes('factory_not_claimed')) return t.errNeedClaim;
    // Raised by claim_factory_by_slug once identity matching is on. This page
    // does not call that function — resolveFactoryEntry routes to /claim — but
    // the claim flow can come BACK here with ?respond=1 and re-raise, and an
    // unmapped code renders as "Something went wrong", which tells a factory
    // nothing about the one thing that would fix it.
    if (m.includes('claim_identity_mismatch')) return t.errIdentityMismatch;
    if (m.includes('claim_no_contact_on_file')) return t.errNoContactOnFile;
    if (m.includes('belongs to another account')) return t.errFactoryOther;
    if (m.includes('already claimed')) return t.errClaimed;
    if (m.includes('already been submitted')) return t.errAlreadyOffer;
    if (m.includes('valid price')) return t.errPrice;
    if (m.includes('register for this request')) return t.errNeedRegister;
    return t.errGeneric;
  };

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [invite, setInvite] = useState(null);      // { status, expired, factory_name, product_name, brief, + structured }
  const [attachUrls, setAttachUrls] = useState({}); // { path: signedUrl }
  // The paths to re-mint after registering. Held in a ref, not read off `invite`,
  // so the re-mint below cannot fire against a stale closure — the auto-continue
  // effect does not list `invite` in its deps and must not have to.
  const attachPathsRef = useRef([]);
  const [phase, setPhase] = useState('brief');      // 'brief' | 'offer' | 'done'
  // Entry gate, resolved before the brief is ever fetched.
  const [gate, setGate] = useState('resolving');    // resolving | allowed | other_account | expired | invalid
  // Where this visitor must go BEFORE they can make an offer, or null if they
  // can make one now: { action, to } from resolveFactoryEntry.
  //
  // This used to be acted on immediately — an unclaimed factory was redirected
  // to /claim the moment the page loaded, and never saw the request (1fcc4a7).
  // Majeed reversed that on 2026-09-16: the request is public and shown first,
  // and claiming is the price of REPLYING, not of reading. So the same
  // destination is now held here and used by the offer button instead.
  const [entry, setEntry] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // The offer form, so the sticky button can reach it and so we can tell whether
  // it is already on screen.
  const offerFormRef = useRef(null);
  // Starts true so the sticky button cannot flash on screen for the frame
  // between the form mounting and the observer's first callback. The observer
  // corrects it immediately; where there is no observer, the effect sets it
  // false so the button appears rather than never existing.
  const [formInView, setFormInView] = useState(true);
  const [isNarrow, setIsNarrow] = useState(false);

  // ═══ THE SHARED QUOTE CORE, 2026-09-21 ════════════════════════════════
  // This door used to collect price / currency / delivery days / MOQ / note
  // and nothing else — no price basis, no origin, no validity, no sample. It
  // writes to the SAME offers table as the other three doors, so a factory
  // quoting through a /f/ invite produced a row the trader's card could only
  // render as «المورد ما حدّد وين يوصّل», permanently. One core, four doors.
  const [offer, setOffer] = useState({
    price: '', currency: 'USD', moq: '', leadTime: '', origin: '', note: '',
    priceBasis: '', validDays: 30,
    sampleAvailable: false, samplePrice: '', sampleDays: '',
  });
  // A basis the supplier picked themselves is never overwritten by a prefill.
  const basisTouchedRef = useRef(false);
  const setOfferField = (k, v) => {
    if (k === 'priceBasis') basisTouchedRef.current = true;
    setOffer((o) => ({ ...o, [k]: v }));
  };
  const [deliveryTerms, setDeliveryTerms] = useState([]);
  const [askCapability, setAskCapability] = useState(false);

  useEffect(() => {
    resolveAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // The supplier's stored delivery capability. Fire-and-forget: a failure
  // costs the prefill and nothing else, and must never stand between a
  // factory and the quote they came here to send.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sess } = await sb.auth.getUser();
      const uid = sess?.user?.id;
      if (!uid || cancelled) return;
      const terms = await loadDeliveryTerms(sb, uid);
      if (cancelled) return;
      setDeliveryTerms(terms);
      setAskCapability(terms.length === 0);
    })();
    return () => { cancelled = true; };
  }, [phase]);

  // Prefill the basis (2026-09-28): what the TRADER asked for first
  // (basisForPreference — door→DDP, port→CIF, factory→EXW), else the
  // supplier's single saved capability, else nothing. Recomputed as either
  // input arrives — they load independently — until the supplier touches the
  // field themselves.
  useEffect(() => {
    if (basisTouchedRef.current) return;
    const pre = prefillPriceBasis(invite?.delivery_preference, defaultBasisFor(deliveryTerms));
    if (pre) setOffer((o) => (o.priceBasis === pre ? o : { ...o, priceBasis: pre }));
  }, [invite?.delivery_preference, deliveryTerms]);

  // Gate first, brief second. Nobody but a signed-in owner gets as far as load().
  async function resolveAndLoad() {
    setLoading(true); setLoadError(false);

    // Session BEFORE the RPC: is_owner is linked_supplier_id = auth.uid(), so a
    // call made before supabase-js restores the session comes back false for a
    // genuine owner — the timing bug fixed on the claim page in ef102ab.
    let signedIn = await readSession();

    // ?respond=1 means this visitor came back from claim or sign-in MOMENTS ago,
    // on a full page load (ClaimFactory uses window.location.assign). A missing
    // session here is therefore far more likely to be supabase-js still
    // restoring than a fact about the visitor — the same race ef102ab fixed on
    // the claim page, which a single getSession() call can still lose.
    //
    // Lose it and the consequence is silent: the resolver says CLAIM, `entry`
    // is set, the auto-continue effect below never fires, and the factory lands
    // on the brief having to press respond a second time with no explanation.
    //
    // So retry, but ONLY when the flag says a session is expected. Bounded, and
    // it changes nothing for an ordinary signed-out visitor: they never carry
    // the flag, so they still resolve on the first read with no added delay.
    if (!signedIn && wantsRespond) {
      for (let i = 0; i < 3 && !signedIn; i += 1) {
        await new Promise((r) => setTimeout(r, 300));
        signedIn = await readSession();
      }
    }

    const { data: rows, error: entryErr } = await sb.rpc('get_factory_invite_entry', { p_slug: slug });
    const entry = Array.isArray(rows) ? rows[0] : rows;
    if (entryErr || !entry) { setGate('invalid'); setLoading(false); return; }

    // Expired first: there is nothing to claim or sign in for, and claim_slug is
    // deliberately NULL once the invite has expired (20260906000001).
    if (entry.expired) { setGate('expired'); setLoading(false); return; }

    const { action, to } = resolveFactoryEntry({
      signedIn,
      alreadyClaimed: !!entry.already_claimed,
      isOwner: !!entry.is_owner,
      claimSlug: entry.claim_slug,
      nextPath: `/f/${slug}`,
    });

    // Signed in as somebody else who already owns this factory: still a wall,
    // because showing them the brief would leak another supplier's work queue.
    if (action === ENTRY.OTHER_ACCOUNT) { setGate('other_account'); setLoading(false); return; }

    // CLAIM and LOGIN no longer redirect. The request is public; these are
    // remembered and applied when the factory tries to RESPOND. ENTRY.OWNER
    // leaves `entry` null, which is what "you can offer right now" means.
    setEntry(action === ENTRY.OWNER ? null : { action, to });
    setGate('allowed');
    await load();
  }

  async function load() {
    setLoading(true);
    setLoadError(false);
    const { data, error } = await sb.rpc('get_factory_invite', { p_slug: slug });
    if (error) { setLoadError(true); setLoading(false); return; }
    const row = Array.isArray(data) ? data[0] : data;
    setInvite(row || null);
    if (row) {
      if (row.status === 'registered') setPhase('offer'); // already registered → straight to offer
      if (row.status === 'offer_submitted') setPhase('done');
      attachPathsRef.current = Array.isArray(row.attachment_paths) ? row.attachment_paths : [];
      mintAttachmentUrls(attachPathsRef.current);
    }
    setLoading(false);
  }

  /**
   * Mint short-lived signed URLs for the attachments (private bucket, slug-gated).
   *
   * Extracted from load() so it can run AGAIN after registering. Two reasons it
   * has to be repeatable:
   *
   *   1. The URLs expire. TTL is 600s in factory-attachment-url, and a factory
   *      that reads the brief, goes off to claim, comes back and then fills in
   *      the offer form can easily outlive one mint.
   *   2. On the return from claim/sign-in the attachments were observed rendering
   *      greyed until a manual reload (2026-09-17 production test). The mechanism
   *      is NOT diagnosed — the function is verify_jwt=false and service-role, so
   *      registering cannot be what unblocks it — which means this is a remedy
   *      whose cause is still open. The three console.error branches below are
   *      what will name it: a 403 is a data problem (paths not on this request),
   *      a 5xx or a throw is an outage or a CORS/network failure.
   *
   * Fire-and-forget on purpose: an attachment that cannot be signed must never
   * stop the factory from reading the brief or making an offer.
   */
  function mintAttachmentUrls(paths) {
    if (!Array.isArray(paths) || paths.length === 0) return;
    // The failure modes look IDENTICAL on screen — attachUrls stays empty and
    // every attachment renders greyed — so they have to be told apart in the
    // console. Swallowing both taught us nothing about either.
    sb.functions.invoke('factory-attachment-url', { body: { slug, paths } })
      .then(({ data, error }) => {
        if (error) {
          // supabase-js turns a non-2xx into an error whose context carries the
          // Response, so the status is what separates 403 from 5xx.
          console.error(
            '[FactoryRequestView] attachment URLs not minted:',
            error.context?.status ?? error.status ?? 'no status',
            error.message || error,
          );
          return;
        }
        if (data?.urls) setAttachUrls(data.urls);
      })
      .catch((e) => {
        // Never reached the function at all: DNS, CORS, offline.
        console.error('[FactoryRequestView] attachment URL request threw:', e?.message || e);
      });
  }

  // One read of the session, never throwing. An unreadable session is treated
  // as signed out — the resolver then offers claim/sign-in, which is the safe
  // direction to be wrong in.
  async function readSession() {
    try {
      const { data } = await sb.auth.getSession();
      return !!data?.session?.user;
    } catch {
      return false;
    }
  }

  async function doRegister() {
    // Ensure the invite is linked to this (now authenticated) supplier.
    const { error: e } = await sb.rpc('register_factory_invite', { p_slug: slug });
    if (e) { setError(mapRpc(e.message)); return false; }
    // Re-mint here rather than at each call site, so every path that registers
    // gets it — the button and the auto-continue on return both go through this.
    mintAttachmentUrls(attachPathsRef.current);
    return true;
  }

  // Only a signed-in owner can reach this button — the gate above guarantees it —
  // so there is no auth branch left. register_factory_invite still binds the
  // invite to them; the factory is already linked, so its bind/seed block is
  // skipped and only the invite row is touched.
  // The gate, moved here from page load. Reading the request costs nothing;
  // replying is what requires an account.
  //
  // `entry.to` is built by resolveFactoryEntry and already carries
  // ?next=/f/<slug>, so the factory lands back here afterwards. We append
  // respond=1 to that next so the return goes straight to the offer form —
  // without it they would have to press this button a second time, because
  // claiming does NOT touch the invite row (register_factory_invite, which sets
  // status='registered', is called only by doRegister below).
  async function onRespond() {
    setError('');

    if (entry) {
      nav(withRespondFlag(entry.to));
      return;
    }

    setBusy(true);
    const ok = await doRegister();
    setBusy(false);
    if (ok) setPhase('offer');
  }

  // Returning from claim or sign-in with ?respond=1: finish what the factory
  // already asked for instead of making them ask again.
  //
  // Guarded on `gate === 'allowed'` and `!entry` so it fires only once the
  // resolver has confirmed this visitor can now register — a stale flag on a
  // still-unclaimed factory must not loop back to the claim page.
  useEffect(() => {
    if (gate !== 'allowed' || entry || loading) return;
    if (!wantsRespond) return;
    if (phase !== 'brief') return;      // already past the brief; nothing to do
    (async () => {
      setBusy(true);
      const ok = await doRegister();
      setBusy(false);
      if (ok) setPhase('offer');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate, entry, loading, wantsRespond]);

  /**
   * Come back from claim at the TOP of the page, not wherever the factory was
   * standing when they left it.
   *
   * ClaimFactory returns here with window.location.assign — a full page load —
   * and nothing in this app resets scroll on the way in: there is no
   * ScrollToTop route component anywhere in src/, and history.scrollRestoration
   * is never touched, so it keeps its default 'auto' and the browser puts the
   * visitor back where they were. Where they were is the Respond button, which
   * sits below the request card, its specs and its attachments — so they land
   * on a bare form with the wordmark, the language switch, and the thing they
   * are being asked to quote on, all scrolled off the top. On a phone that is
   * the whole page.
   *
   * THE FORM STILL OPENS BY ITSELF. Only the viewport moves. The sticky button
   * is what carries them back down to it.
   *
   * Scoped to ?respond=1 deliberately. Pressing Respond by hand opens the form
   * exactly where the finger already is, and yanking that page to the top would
   * be the same defect facing the other way.
   *
   * 'manual' is set as well as scrolling, because restoration fires around the
   * load event — later than this effect — so scrolling without it would be
   * undone a moment after it happened.
   */
  useLayoutEffect(() => {
    if (!wantsRespond) return undefined;
    const prior = window.history.scrollRestoration;
    try { window.history.scrollRestoration = 'manual'; } catch { /* unsupported — the scroll below still runs */ }
    window.scrollTo(0, 0);
    return () => { try { window.history.scrollRestoration = prior || 'auto'; } catch { /* noop */ } };
  }, [wantsRespond]);

  // Has the factory taken control of the scroll themselves? Once they have,
  // nothing below may move the page again — a page that scrolls itself while
  // someone is reading it is worse than one that starts in the wrong place.
  const userScrolledRef = useRef(false);
  useEffect(() => {
    const mark = () => { userScrolledRef.current = true; };
    window.addEventListener('touchmove', mark, { passive: true });
    window.addEventListener('wheel', mark, { passive: true });
    window.addEventListener('keydown', mark);
    return () => {
      window.removeEventListener('touchmove', mark);
      window.removeEventListener('wheel', mark);
      window.removeEventListener('keydown', mark);
    };
  }, []);

  /**
   * Re-assert the top once the brief has actually rendered.
   *
   * This link is opened from WhatsApp's in-app browser on iOS, not Safari — a
   * WKWebView, where the page's height changes twice after our layout effect
   * has already run: once when the RPC returns and the request card appears,
   * and again when the attachment thumbnails decode and take up space. A
   * viewport placed at the top before that content exists is not necessarily
   * still at the top afterwards.
   *
   * history.scrollRestoration is a WebKit API, so the in-app browser has
   * whatever the iOS version's Safari has — which version that starts at was
   * NOT checked against anything, and is why the assignment is wrapped in
   * try/catch rather than gated on a version. Either way it governs only the
   * browser's OWN restoration. It does not govern reflow, which is why this
   * second pass is here rather than being redundant.
   *
   * Guarded on userScrolledRef so it is silent the instant the factory touches
   * the screen, and on `once` so a later re-render cannot re-fire it.
   */
  const toppedRef = useRef(false);
  useEffect(() => {
    if (!wantsRespond || loading || toppedRef.current) return;
    if (userScrolledRef.current) { toppedRef.current = true; return; }
    toppedRef.current = true;
    window.scrollTo(0, 0);
  }, [wantsRespond, loading]);

  // Phone-sized viewports only. On a desktop width the form is either visible
  // or one short scroll away, and a floating bar would be clutter.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(max-width: 640px)');
    const read = () => setIsNarrow(mq.matches);
    read();
    mq.addEventListener('change', read);
    return () => mq.removeEventListener('change', read);
  }, []);

  /**
   * Hide the sticky button once the form is actually on screen.
   *
   * Not a refinement — without it the bar sits over the form's own Submit
   * button exactly while the factory is filling the form in, which is a worse
   * obstruction than the one this whole change removes.
   *
   * No IntersectionObserver (old Android WebView) means the button simply stays
   * visible: still useful, never covering anything it was not already covering.
   */
  useEffect(() => {
    const el = offerFormRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver !== 'function') { setFormInView(false); return undefined; }
    const io = new IntersectionObserver(
      ([e]) => setFormInView(e.isIntersecting),
      { rootMargin: '-80px 0px 0px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [phase]);

  async function handleSubmitOffer() {
    // THE SAME RULE THE OTHER THREE DOORS RUN. "Same fields" has to mean the
    // same validation, or the doors diverge again by the back way.
    const bad = validateQuoteCore({
      price: offer.price, leadTime: offer.leadTime, priceBasis: offer.priceBasis,
    });
    if (bad) { setError(qt[bad]); return; }
    setBusy(true); setError('');

    // Capability first, so it is answered once even if the quote is refused.
    if (askCapability && deliveryTerms.length > 0) {
      const { data: sess } = await sb.auth.getUser();
      if (sess?.user?.id) await saveDeliveryTerms(sb, sess.user.id, deliveryTerms);
    }

    const sampleOn = !!offer.sampleAvailable;
    const { error: e } = await sb.rpc('submit_factory_offer', {
      p_slug: slug,
      p_price: Number(offer.price),
      p_currency: offer.currency,
      p_delivery_days: Number.parseInt(offer.leadTime, 10),
      p_moq: offer.moq.trim() || null,
      p_note: offer.note.trim() || null,
      p_price_basis: offer.priceBasis || null,
      p_valid_days: offer.validDays ? Number(offer.validDays) : null,
      p_origin: offer.origin.trim() || null,
      p_sample_available: sampleOn,
      p_sample_price: sampleOn && String(offer.samplePrice).trim() !== '' ? Number(offer.samplePrice) : null,
      p_sample_days: sampleOn && String(offer.sampleDays).trim() !== '' ? Number.parseInt(offer.sampleDays, 10) : null,
    });
    setBusy(false);
    if (e) { setError(mapRpc(e.message)); return; }
    setPhase('done');
  }

  // ⚠️ THE TOP PADDING CLEARS THE FIXED NAVBAR, and it is not decoration.
  //
  // `nav` is `position: fixed; top: 0; height: var(--page-top-offset)`
  // (index.css) and /f/:slug is NOT in isChromelessPage (App.js) — unlike
  // /claim/, /factory/inbox/ and /factory-chat/, which are. So the global bar
  // is painted over the top of this page, and a flat 32px put this page's own
  // wordmark and language switch underneath it: at a perfectly correct scroll
  // position of 0, the first thing the factory saw was the request card, with
  // the header cut off above it.
  //
  // Every other full-page wrapper in the app already does this —
  // .dashboard-wrap, .product-detail-wrap, .profile-wrap, .about-wrap,
  // .supplier-landing all carry padding-top: var(--page-top-offset). This page
  // was the one that did not, because it sets its padding inline.
  //
  // It was invisible until the scroll fix landed: the browser used to restore
  // the visitor half way down the page, so nobody ever looked at y=0.
  const wrap = { maxWidth: 560, margin: '0 auto', padding: 'calc(var(--page-top-offset) + 24px) 20px 60px' };
  const card = { background: 'var(--bg-subtle, #faf9f7)', border: '1px solid var(--border-muted, #e8e5de)', borderRadius: 14, padding: 24, marginBottom: 16 };
  const label = { display: 'block', fontSize: 12, color: 'var(--text-secondary, #6b6560)', marginBottom: 6 };
  const input = { width: '100%', padding: '10px 12px', border: '1px solid var(--border-muted, #e8e5de)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 12 };
  const btn = { background: '#1a1814', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 22px', fontSize: 14, cursor: 'pointer' };
  const link = { background: 'none', border: 'none', color: '#8B7355', fontSize: 12, cursor: 'pointer', textDecoration: 'underline', padding: 0 };

  // Structured specs (identity-safe fields the RPC returns) + attachments.
  // Category FIRST, because it is the question the reader asks before any other:
  // is this for me at all? It matters most for a factory that reached this page
  // from a shared link rather than a message addressed to it.
  //
  // The RPC returns the raw code (electronics, bedroom_furniture). The display
  // group is resolved here from the same table the console uses, so the two
  // never drift — and a code with no group falls back to itself rather than
  // vanishing, because an unmapped category is a data question and hiding it
  // answers nothing.
  const catRow = (() => {
    if (!invite?.category) return null;
    const g = displayCategoryForCode(invite.category);
    return { k: t.category, v: g ? (g.label?.[lang] || g.label?.en || invite.category) : invite.category };
  })();

  // The trader's terms come from the shared builder (2026-09-28) — the same
  // rows and wording every supplier door shows: quantity + unit, target price,
  // payment plan, the delivery ask naming the Incoterm to quote, sample,
  // destination, timeframe. Target price and payment plan arrive only once
  // get_factory_invite returns them (docs/proposed-sql/2b_supplier_sees_terms
  // .sql); until then those two rows simply do not render. No identity field
  // is read — buyer_name stays NULL in the RPC (M8).
  const termRows = invite ? supplierTermRows(invite, lang).map((r) => ({ key: r.key, k: r.label, v: r.value })) : [];
  const specsRows = invite ? [
    catRow,
    ...termRows.filter((r) => r.key === 'quantity'),
    (invite.customization_types && invite.customization_types.length)
      ? { k: t.customization, v: invite.customization_types.map((k) => { const c = CUSTOMIZATION_CHIPS.find((x) => x.key === k); return c ? labelFor(c, lang) : k; }).join(', ') } : null,
    ...termRows.filter((r) => r.key !== 'quantity'),
  ].filter(Boolean) : [];
  // The request's own title, which the page never showed — the factory saw their
  // own name as the heading and had to infer the subject from the brief.
  //
  // No Arabic column is needed and none is requested from the RPC: all three
  // writers set title_en to `titleEn || titleAr || fallbackTitle`
  // (createManagedRequest.js, Requests.jsx, IdeaToProduct.jsx), so an
  // Arabic-only request carries its Arabic string in title_en and renders as-is.
  const requestTitle = invite
    ? (lang === 'zh' ? invite.request_title_zh : invite.request_title_en)
      || invite.request_title_en || invite.request_title_zh || ''
    : '';

  // Prefer the factory's OWN language for the brief. The RPC returns all three
  // (each already scrubbed by mask_contact_details), but the page was rendering
  // only `brief` — the base column, which is Arabic for an Arabic trader — so a
  // Chinese factory was reading Arabic while brief_zh sat unused.
  //
  // ⚠️ Known remaining case: brief_en/brief_zh coalesce down to supplier_brief
  // when there is no AI brief at all, so a brief-less request still shows Arabic.
  // Closing that needs get_factory_invite to fall back to
  // requests.description_en/description_zh — a DB change, logged in
  // docs/TECH_DEBT.md and deliberately not made here.
  const briefText = invite
    ? ((lang === 'zh' ? invite.brief_zh : invite.brief_en) || invite.brief || '')
    : '';

  const attachments = invite && Array.isArray(invite.attachment_paths) ? invite.attachment_paths : [];
  const fileName = (p) => { const seg = decodeURIComponent(String(p).split('/').pop() || ''); const i = seg.indexOf('_'); return i >= 0 ? seg.slice(i + 1) : seg; };
  const isImg = (n) => /\.(jpe?g|png|webp|gif)$/i.test(n);

  return (
    /* DIRECTION FOLLOWS THE CARD, NOT THE ACCOUNT (2026-09-21).
       This page carries its OWN language — the EN / 中文 toggle below, `lang`
       state, default 'en' — and it set no dir at all, so it inherited one from
       App's wrapper, which follows the signed-in account. An Arabic-account
       supplier reading the English card got an RTL layout: text right-aligned,
       and the full stop in "Buyer contact details are shared only after the
       deal proceeds through Maabar." rendered at the START of the line.
       Derived from `lang` rather than hardcoded 'ltr' even though the toggle
       offers only en and zh today: hardcoding would be correct now and silently
       wrong the moment Arabic is added to that toggle, which is exactly the
       kind of thing this page would get.
       PublicRfqQuote (/rfq/:token/quote) does NOT have this bug — it has no
       content toggle, so its dir and its content read the same `lang`. */
    <div dir={lang === 'ar' ? 'rtl' : 'ltr'} style={{ background: '#f5f3ef', minHeight: '100dvh' }}>
      <div style={wrap}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontSize: 18, fontWeight: 600, color: '#1a1814' }}>مَعبر · Maabar</span>
          <div style={{ display: 'flex', gap: 5 }}>
            {['en', 'zh'].map((l) => (
              <button key={l} onClick={() => setLang(l)} style={{
                background: lang === l ? '#1a1814' : '#fff', color: lang === l ? '#fff' : '#6b6560',
                border: `1px solid ${lang === l ? '#1a1814' : '#e2ddd3'}`, borderRadius: 99,
                padding: '6px 15px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>{l === 'en' ? 'EN' : '中文'}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <p style={{ color: 'var(--text-secondary)' }}>…</p>
        ) : gate === 'invalid' ? (
          <div style={card}><p>{t.notFound}</p></div>
        ) : gate === 'expired' ? (
          <div style={card}><p>{t.expired}</p></div>
        ) : gate === 'other_account' ? (
          <div style={card}>
            <h2 style={{ fontSize: 17, color: '#1a1814', margin: '0 0 8px' }}>{t.otherTitle}</h2>
            <p style={{ fontSize: 13, color: '#6b6560', lineHeight: 1.7, margin: '0 0 16px' }}>{t.otherBody}</p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button style={btn} onClick={async () => { await sb.auth.signOut(); window.location.reload(); }}>{t.otherSignout}</button>
              <a style={link} href={waLink(t.waText)} target="_blank" rel="noreferrer">{t.otherSupport}</a>
            </div>
          </div>
        ) : loadError ? (
          <div style={card}><p>{t.errLoad}</p></div>
        ) : !invite ? (
          <div style={card}><p>{t.notFound}</p></div>
        ) : invite.expired ? (
          <div style={card}><p>{t.expired}</p></div>
        ) : invite.request_open === false ? (
          /* THE REQUEST IS OVER. Gated HERE, before the brief and the form,
             because the server-side refusal comes too late to be useful:
             submit_factory_offer does reject a closed request, but it checks
             registration FIRST, so a factory filled in the whole form and was
             told to "register before submitting an offer" -- the wrong reason
             entirely, and one they cannot act on.

             ⚠️ STRICTLY === false, NOT falsy. request_open arrives from
             get_factory_invite, which only gained the column on 2026-09-20. If
             this page is ever served against an older function -- a rollback, a
             stale deploy, a rebuilt database -- the field is UNDEFINED, and a
             falsy test would close EVERY request on the page. Strict false
             means "the server said no"; anything else means "the server did not
             say", which behaves exactly as it did before this existed. */
          <div style={card}>
            <h2 style={{ fontSize: 17, color: '#1a1814', margin: '0 0 8px' }}>{t.overTitle}</h2>
            <p style={{ fontSize: 13, color: '#6b6560', lineHeight: 1.7, margin: 0 }}>{t.overBody}</p>
          </div>
        ) : (
          <>
            <div style={card}>
              <p style={{ fontSize: 12, color: '#b0ab9e', letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 6px' }}>{t.heading} · {t.from}</p>
              <h1 style={{ fontSize: 20, color: '#1a1814', margin: '0 0 16px' }}>{invite.factory_name || 'Maabar'}</h1>
              <p style={{ ...label }}>{t.details}</p>
              {(requestTitle || invite.request_ref) && (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', margin: '0 0 10px' }}>
                  {requestTitle && <h2 style={{ fontSize: 16.5, fontWeight: 600, color: '#1a1814', margin: 0 }}>{requestTitle}</h2>}
                  {invite.request_ref && (
                    <span dir="ltr" style={{
                      fontSize: 11.5, color: '#6b6560', background: '#fff', border: '1px solid #e8e5de',
                      borderRadius: 99, padding: '2px 9px', whiteSpace: 'nowrap',
                    }}>{invite.request_ref}</span>
                  )}
                </div>
              )}
              {invite.product_name && <p style={{ fontSize: 14, color: '#1a1814', margin: '0 0 8px' }}><strong>{t.product}:</strong> {invite.product_name}</p>}
              <p style={{ fontSize: 14, color: '#3d3a35', lineHeight: 1.8, whiteSpace: 'pre-wrap', margin: 0 }}>{briefText || invite.product_name || ''}</p>

              {specsRows.length > 0 && (
                <div style={{ marginTop: 16, borderTop: '1px solid #e8e5de', paddingTop: 14 }}>
                  <p style={{ ...label, marginBottom: 8 }}>{t.specs}</p>
                  {specsRows.map((r, i) => (
                    <p key={i} style={{ fontSize: 13.5, color: '#1a1814', margin: '0 0 6px' }}>
                      <span style={{ color: '#6b6560' }}>{r.k}:</span> {r.v}
                    </p>
                  ))}
                </div>
              )}

              {attachments.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <p style={{ ...label, marginBottom: 8 }}>{t.attachments} ({attachments.length})</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {attachments.map((p, i) => {
                      const name = fileName(p); const url = attachUrls[p]; const img = isImg(name);
                      return (
                        <a key={i} href={url || undefined} target="_blank" rel="noreferrer" title={name}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: img ? 72 : 'auto', height: 72, maxWidth: 200, padding: img ? 0 : '0 12px',
                            border: '1px solid #e8e5de', borderRadius: 8, overflow: 'hidden',
                            background: '#fff', textDecoration: 'none', color: '#3d3a35', fontSize: 12,
                            cursor: url ? 'pointer' : 'default', opacity: url ? 1 : 0.5,
                          }}>
                          {img && url
                            ? <img src={url} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              <p style={{ fontSize: 11.5, color: '#b0ab9e', marginTop: 16 }}>{t.privacy}</p>
            </div>

            {phase === 'done' || invite.status === 'offer_submitted' ? (
              <div style={card}>
                <h2 style={{ fontSize: 17, color: '#1a1814', margin: '0 0 8px' }}>{t.doneT}</h2>
                <p style={{ fontSize: 13, color: '#6b6560', lineHeight: 1.7, margin: 0 }}>{phase === 'done' ? t.doneB : t.already}</p>
              </div>
            ) : phase === 'brief' ? (
              <>
                <button style={btn} onClick={onRespond} disabled={busy}>{busy ? t.working : t.respond}</button>
                {entry && (
                  <p style={{ fontSize: 12, color: 'var(--text-secondary, #6b6560)', margin: '10px 0 0', lineHeight: 1.7 }}>
                    {entry.action === ENTRY.LOGIN ? t.respondNoteLogin : t.respondNoteClaim}
                  </p>
                )}
              </>
            ) : phase === 'offer' ? (
              <div style={card} ref={offerFormRef}>
                <h2 style={{ fontSize: 17, color: '#1a1814', margin: '0 0 16px' }}>{t.offerTitle}</h2>
                {askCapability && (
                  <DeliveryCapability
                    lang={lang}
                    value={deliveryTerms}
                    onChange={setDeliveryTerms}
                    t={{ title: qt.capTitle, hint: qt.capHint }}
                    styles={{ label }}
                  />
                )}
                <QuoteFields
                  t={qt}
                  idPrefix="fri"
                  cnyPerUsd={cnyPerUsd}
                  allowedBases={allowedBasesFor(deliveryTerms)}
                  askSample={SAMPLE_ASKED.includes(String(invite?.sample_requirement || ''))}
                  sampleRequirement={invite?.sample_requirement || ''}
                  deliveryAsk={deliveryAskText(invite?.delivery_preference, lang)}
                  basisWarning={deliveryShortfallText(offer.priceBasis, invite?.delivery_preference, lang)}
                  styles={{ label, input, hint: { margin: '4px 0 0', fontSize: 12, color: '#7a7268', lineHeight: 1.6 } }}
                  values={offer}
                  onChange={setOfferField}
                />
                {!!error && <p style={{ color: '#a05050', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
                <button style={btn} onClick={handleSubmitOffer} disabled={busy}>{busy ? t.submitting : t.submit}</button>
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* ── The way back down to the form ──────────────────────────────────
          The page now opens at the top, which is the point: the factory sees
          what they are quoting on before they are asked for a price. This is
          how they get to the form without hunting for it.

          It names the form's own heading (t.offerTitle) rather than carrying a
          second string, so the button and the thing it scrolls to can never
          come to say different words in either language.

          Bottom offset clears the WhatsApp in-app browser's toolbar via the
          safe-area inset; without it the pill sits underneath that bar on an
          iPhone and cannot be tapped. */}
      {phase === 'offer' && isNarrow && !formInView && (
        <button
          type="button"
          onClick={() => offerFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          style={{
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            zIndex: 20,
            background: '#1a1814',
            color: '#fff',
            border: 'none',
            borderRadius: 99,
            padding: '11px 20px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(26,24,20,0.28)',
            maxWidth: 'calc(100vw - 40px)',
            whiteSpace: 'nowrap',
          }}
        >
          {t.offerTitle} ↓
        </button>
      )}
    </div>
  );
}
